import { ArrowLeft, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PosUserForm } from '@/components/features/pos-users/PosUserForm'
import { PosUserTypePermissions } from '@/components/features/pos-users/PosUserTypePermissions'
import { useWriteAccess } from '@/hooks/use-permissions'
import { useCreatePosUser } from '@/hooks/use-pos-users'
import { type PosUserFormData } from '@/lib/validation/pos-user-schemas'
import { type CreatePosUserRequest } from '@/models/domain/pos-user'

function PosCreateUserDescription() {
  return (
    <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-sm text-gray-700 dark:text-gray-300 space-y-2">
      <p>POS Users are used for logging into the POS System.</p>
      <p>
        You will need to populate different fields based on the POS User Type. District Managers
        require a District, while Store Managers and Clerks require a Location. Before creating a
        POS User of these Types, you must create at least one District and one Location.
      </p>
      <PosUserTypePermissions />
    </div>
  )
}

function buildCreateRequest(data: PosUserFormData): CreatePosUserRequest {
  const request: CreatePosUserRequest = {
    id: data.id,
    first_name: data.first_name,
    last_name: data.last_name,
    level: data.level,
    active: data.active,
    expires: data.expires ? new Date(data.expires).toISOString() : null,
    expires_eos: data.expires_eos,
  }

  if (data.email) request.email = data.email
  if (data.pin) request.pin = data.pin
  if (data.location_id) request.location_id = data.location_id
  if (data.district_id) request.district_id = data.district_id

  return request
}

export function CreatePosUserPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const createMutation = useCreatePosUser()
  const canWrite = useWriteAccess('cs_pos_users_w')

  const handleSubmit = async (data: PosUserFormData) => {
    setError(null)

    try {
      await createMutation.mutateAsync(buildCreateRequest(data))
      navigate('/admin/pos-users', {
        state: { success: `POS user ${data.first_name} ${data.last_name} created successfully!` },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred')
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => {
            navigate('/admin/pos-users')
          }}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to POS users
        </button>

        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
            <UserPlus className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create POS User</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Add a new POS terminal user</p>
          </div>
        </div>
      </div>

      <PosCreateUserDescription />

      <PosUserForm
        mode="create"
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending}
        error={error}
        canWrite={canWrite}
      />
    </div>
  )
}
